import Skeleton from "react-loading-skeleton";
import { useAppSelector } from "store/hooks";
import { useUserData } from "store/slices/authSlice";
import { usePortalSettingsData } from "store/slices/dashboardSlice";
import { RootState } from "store/store";
import {
  cardToShortname,
  formatCurrency,
  getPaymentMethod,
} from "utils/HelperFunctions";
import { useTid } from "utils/testid";

const PaymentConfirmation = ({
  currentStep,
  futurePayment,
  paymentDetails,
  selectedMethod,
  dataPayment,
  isAddNewCard,
  values,
  iconType,
}: any) => {
  const tid = useTid();
  const userData = useUserData();
  const accountData = userData?.data?.accountData;
  const { conFee, isFeeLoading } = useAppSelector(
    (state: RootState) => state.payment
  );
  const portalSettings = usePortalSettingsData();

  return (
    <div
      // Tight top padding: the method list above can fill the drawer, and the
      // panel itself does not scroll, so spare padding here pushed this step
      // off the bottom edge where it could not be reached at all.
      className="lg:text-xl lg:pt-1.5 text-base lg:h-fit h-full font-medium"
      data-testid={tid("payment-confirmation")}
      aria-label="Payment confirmation"
    >
      <p
        data-testid={tid("payment-confirmation-title")}
        className={`${currentStep === 2 ? "text-btnPrimaryActive" : "text-txtPrimary"}`}
      >
        Payment confirmation
      </p>
      <div
        className={`${window.innerWidth > 769 && "answerSection1"} ${currentStep === 2 ? "open !h-fit" : ""}`}
      >
        {futurePayment ? (
          <div className="lg:pt-10 pt-[30px] lg:pb-0 pb-9 flex flex-col gap-10">
            <div
              className="lg:text-xl text-base text-txtSecondary font-normal"
              data-testid={tid("payment-confirmation-retry-message")}
            >
              Would you like to retry the failed{" "}
              {formatCurrency(parseFloat(paymentDetails?.amount) + conFee) ||
                "$0.00"}{" "}
              payment and update the payment method for future transactions?
            </div>
          </div>
        ) : (
          <>
            <div className="pt-10 flex flex-col gap-5">
              <div className="w-full flex">
                <span className="text-sm font-bold text-txtPrimary uppercase leading-[18.23px]">
                  Billed to
                </span>
              </div>
              <div className="w-full h-[1px] bg-strokePrimary" />
              <div
                className="flex justify-between items-center text-sm leading-[18.23px] w-full"
                data-testid={tid("payment-confirmation-account-id")}
              >
                <p className="text-txtSecondary">Account ID</p>
                <p className="text-txtPrimary">{accountData?.accountid}</p>
              </div>
              <div
                className="flex justify-between items-center text-sm leading-[18.23px] w-full"
                data-testid={tid("payment-confirmation-customer")}
              >
                <p className="text-txtSecondary">Customer</p>
                <p className="text-txtPrimary">
                  {(accountData?.first_name || "") +
                    " " +
                    (accountData?.last_name || "") || "--"}
                </p>
              </div>
              <div
                className="flex justify-between items-center text-sm leading-[18.23px] w-full"
                data-testid={tid("payment-confirmation-method")}
              >
                <p className="text-txtSecondary">Payment method</p>
                <p className="text-txtPrimary">
                  {isAddNewCard
                    ? selectedMethod === "Card"
                      ? getPaymentMethod({
                          card_type: dataPayment?.selectedCard?.cc_type,
                          method: "CC",
                          digits: dataPayment?.selectedCard?.cc_4digit,
                          account_number:
                            dataPayment?.selectedCard?.account_number,
                          className: "",
                          textClassName: "!text-txtSecondary",
                          isHidden: false,
                          rowId: "selected-card"
                        })
                      : selectedMethod === "Bank"
                        ? getPaymentMethod({
                            card_type: dataPayment?.selectedBank?.cc_type,
                            method: "ACH",
                            digits: dataPayment?.selectedBank?.cc_4digit,
                            account_number:
                              dataPayment?.selectedBank?.account_number,
                            className: "",
                            textClassName: "!text-txtSecondary",
                            isHidden: false,
                            bank_name: dataPayment?.selectedBank?.bank_name,
                            rowId: "selected-bank"
                          })
                        : getPaymentMethod({
                            card_type: dataPayment?.selectedWallet?.cc_type,
                            method: dataPayment?.selectedWallet?.payment_method,
                            digits: dataPayment?.selectedWallet?.cc_4digit,
                            account_number:
                              dataPayment?.selectedWallet?.account_number,
                            className: "",
                            textClassName: "!text-txtSecondary",
                            isHidden: false,
                            
                          })
                    : getPaymentMethod({
                        card_type: iconType,
                        method: cardToShortname(selectedMethod),
                        digits: values?.x_card_num
                          ?.replaceAll(" ", "")
                          .slice(-4),
                        account_number: values?.account_number,
                        className: "",
                        textClassName: "!text-txtSecondary",
                        isHidden: false,
                        bank_name: values?.bank_name || "",
                        rowId: "new-bank"
                      })}
                </p>
              </div>
              <div className="w-full h-[1px] bg-strokePrimary" />
              <div className="flex gap-4 w-full flex-col">
                <div
                  className="flex justify-between items-center text-sm leading-[18.23px] w-full"
                  data-testid={tid("payment-confirmation-amount")}
                >
                  <p className="text-txtPrimary">Amount</p>
                  <p className="text-txtPrimary">
                    {formatCurrency(paymentDetails?.amount) || "$0.00"}
                  </p>
                </div>
                {conFee !== 0 || isFeeLoading ? (
                  <div
                    className="flex justify-between items-center text-sm leading-[18.23px] w-full font-medium"
                    data-testid={tid("payment-confirmation-fee")}
                  >
                    <p className="text-txtPrimary">
                      {portalSettings?.cc_fee_label
                        ? portalSettings?.cc_fee_label
                        : "Convenience fee"}
                    </p>
                    <p className="text-txtPrimary">
                      {isFeeLoading ? (
                        <Skeleton height={13} width={90} />
                      ) : (
                        formatCurrency(conFee) || "$0.00"
                      )}
                    </p>
                  </div>
                ) : null}
              </div>
              <div className="w-full h-[1px] bg-strokePrimary" />
              <div
                className="flex justify-between items-center text-xl font-bold leading-[18.23px] w-full"
                data-testid={tid("payment-confirmation-total")}
              >
                <p className="text-txtPrimary uppercase">TOTAL</p>
                <p className="text-txtPrimary text-2xl">
                  {formatCurrency(
                    parseFloat(paymentDetails?.amount) + conFee
                  ) || "$0.00"}
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentConfirmation;
